import React, { useMemo, useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { TrendingUp, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

const SERIES_COLORS = [
  '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4',
  '#ec4899', '#84cc16', '#f97316', '#6366f1',
];

interface DatadogSeries {
  metric?: string;
  display_name?: string;
  scope?: string;
  tag_set?: string[];
  pointlist?: [number, number | null][];
  unit?: [{ name?: string; scale_factor?: number }];
  expression?: string;
}

interface DatadogMetricResult {
  query: string;
  series: DatadogSeries[];
}

interface DatadogMetricsChartProps {
  metrics: DatadogMetricResult[];
}

function extractSeriesLabel(series: DatadogSeries): string {
  if (series.scope && series.scope !== '*') {
    const parts = series.scope.split(',').map((p) => p.trim());

    // Prefer pod_name if present — it's the most identifying tag
    const podPart = parts.find((p) => p.startsWith('pod_name:'));
    if (podPart) {
      return podPart.substring('pod_name:'.length);
    }

    // Otherwise show all tags except cluster/namespace (redundant context)
    const meaningful = parts
      .filter((p) => !p.startsWith('kube_cluster_name:') && !p.startsWith('kube_namespace:'))
      .map((p) => {
        const idx = p.indexOf(':');
        return idx >= 0 ? p.substring(idx + 1) : p;
      });

    if (meaningful.length > 0) return meaningful.join(', ');

    // Last resort: show all tag values
    return parts.map((p) => {
      const idx = p.indexOf(':');
      return idx >= 0 ? p.substring(idx + 1) : p;
    }).join(', ');
  }
  return series.display_name || series.metric || 'series';
}

function formatNanocores(value: number): string {
  if (value >= 1_000_000_000) return (value / 1_000_000_000).toFixed(2) + ' cores';
  if (value >= 1_000_000) return (value / 1_000_000).toFixed(1) + 'm';
  if (value >= 1_000) return (value / 1_000).toFixed(0) + 'µ';
  return value.toFixed(0) + 'n';
}

function formatMetricValue(value: number, unit?: string): string {
  if (unit === 'nanocore' || unit === 'nanocores') return formatNanocores(value);
  if (unit === 'percent' || unit === '%') return value.toFixed(1) + '%';
  if (unit === 'byte' || unit === 'bytes') {
    if (value >= 1_073_741_824) return (value / 1_073_741_824).toFixed(2) + ' GB';
    if (value >= 1_048_576) return (value / 1_048_576).toFixed(1) + ' MB';
    if (value >= 1024) return (value / 1024).toFixed(0) + ' KB';
    return value.toFixed(0) + ' B';
  }
  if (value >= 1_000_000) return (value / 1_000_000).toFixed(2) + 'M';
  if (value >= 1_000) return (value / 1_000).toFixed(1) + 'K';
  return value.toFixed(2);
}

function formatQueryTitle(query: string): string {
  const match = query.match(/^(?:avg|sum|max|min):([^{]+)/);
  if (match) {
    return match[1].replace(/_/g, ' ').replace(/\./g, ' > ');
  }
  return query;
}

interface ChartTooltipProps {
  active?: boolean;
  payload?: Array<{ value: number; name: string; color: string }>;
  label?: string;
  unit?: string;
}

const ChartTooltip: React.FC<ChartTooltipProps> = ({ active, payload, label, unit }) => {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div className="bg-popover border rounded-md shadow-lg p-2 text-xs max-w-[420px] z-50" style={{ opacity: 1 }}>
      <p className="text-muted-foreground mb-1">{label}</p>
      {payload.map((entry, i) => (
        <div key={i} className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: entry.color }} />
          <span className="break-all">{entry.name}</span>
          <span className="font-medium ml-auto pl-2 whitespace-nowrap">{formatMetricValue(entry.value, unit)}</span>
        </div>
      ))}
    </div>
  );
};

const MAX_RENDERED_SERIES = 30;
const MAX_LEGEND_ITEMS = 8;

const SingleMetricChart: React.FC<{ metric: DatadogMetricResult }> = React.memo(({ metric }) => {
  const [page, setPage] = useState(0);

  // Sort all series by data coverage (most data points first) — stable across pages
  const allSorted = useMemo(() => {
    return (metric.series || [])
      .filter((s) => s.pointlist && s.pointlist.length > 0)
      .sort((a, b) => (b.pointlist?.length || 0) - (a.pointlist?.length || 0));
  }, [metric]);

  const totalSeriesCount = allSorted.length;
  const totalPages = Math.ceil(totalSeriesCount / MAX_RENDERED_SERIES);

  const { seriesWithData, seriesLabels, chartData, effectiveUnit } = useMemo(() => {
    const start = page * MAX_RENDERED_SERIES;
    const pageSlice = allSorted.slice(start, start + MAX_RENDERED_SERIES);

    const labels = pageSlice.map((s) => extractSeriesLabel(s));

    const unit = pageSlice[0]?.unit?.[0]?.name || '';
    const isNanocores = unit === 'nanocore' || metric.query?.includes('cpu.usage');

    const tsSet = new Set<number>();
    for (const s of pageSlice) {
      for (const [ts] of s.pointlist || []) {
        tsSet.add(ts);
      }
    }
    const sortedTs = Array.from(tsSet).sort((a, b) => a - b);

    const seriesMaps = pageSlice.map((s) => {
      const map = new Map<number, number | null>();
      for (const [ts, val] of s.pointlist || []) {
        map.set(ts, val);
      }
      return map;
    });

    // Include date in x-axis labels when data spans more than 24 hours
    const spanMs = sortedTs.length >= 2 ? sortedTs[sortedTs.length - 1] - sortedTs[0] : 0;
    const multiDay = spanMs > 24 * 60 * 60 * 1000;

    const data = sortedTs.map((ts) => {
      const d = new Date(ts);
      const timeLabel = multiDay
        ? d.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ' ' +
          d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      const point: Record<string, string | number | null> = {
        time: timeLabel,
        fullTimestamp: d.toISOString(),
      };
      for (let i = 0; i < pageSlice.length; i++) {
        point[`series_${i}`] = seriesMaps[i].get(ts) ?? null;
      }
      return point;
    });

    return {
      seriesWithData: pageSlice,
      seriesLabels: labels,
      chartData: data,
      effectiveUnit: isNanocores ? 'nanocore' : unit,
    };
  }, [metric, allSorted, page]);

  if (totalSeriesCount === 0) {
    return null;
  }

  return (
    <Card className="my-3 border-border/50 relative hover:z-10">
      <CardHeader className="pb-2 pt-3 px-4">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-sm font-medium min-w-0">
            <TrendingUp className="h-4 w-4 text-blue-500 flex-shrink-0" />
            <span className="truncate text-muted-foreground">{formatQueryTitle(metric.query)}</span>
          </CardTitle>
          {totalPages > 1 && (
            <div className="flex items-center gap-1.5 ml-3 flex-shrink-0">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="h-7 w-7 p-0"
              >
                <ChevronLeft className="h-3.5 w-3.5" />
              </Button>
              <span className="text-xs text-muted-foreground whitespace-nowrap">
                {page + 1} / {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page === totalPages - 1}
                className="h-7 w-7 p-0"
              >
                <ChevronRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="pb-3 px-4">
        <ResponsiveContainer width="100%" height={seriesWithData.length > 1 && seriesWithData.length <= MAX_LEGEND_ITEMS ? 280 : 220}>
          <LineChart data={chartData} margin={{ top: 15, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis
              dataKey="time"
              fontSize={10}
              tickLine={false}
              axisLine={false}
              className="text-muted-foreground"
              interval="preserveStartEnd"
            />
            <YAxis
              fontSize={10}
              tickLine={false}
              axisLine={false}
              className="text-muted-foreground"
              tickFormatter={(v) => formatMetricValue(v, effectiveUnit)}
              width={55}
            />
            <Tooltip content={<ChartTooltip unit={effectiveUnit} />} wrapperStyle={{ zIndex: 50 }} />
            {seriesWithData.length > 1 && seriesWithData.length <= MAX_LEGEND_ITEMS && (
              <Legend
                formatter={(_, entry) => {
                  const idx = parseInt((entry.dataKey as string)?.replace('series_', '') || '0');
                  return <span className="text-xs">{seriesLabels[idx]}</span>;
                }}
                iconSize={8}
                wrapperStyle={{ fontSize: '11px', paddingTop: '4px' }}
              />
            )}
            {seriesWithData.map((_, i) => (
              <Line
                key={i}
                type="monotone"
                dataKey={`series_${i}`}
                name={seriesLabels[i]}
                stroke={SERIES_COLORS[i % SERIES_COLORS.length]}
                strokeWidth={1.5}
                dot={false}
                activeDot={{ r: 3, fill: SERIES_COLORS[i % SERIES_COLORS.length] }}
                connectNulls
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
        {totalSeriesCount > MAX_LEGEND_ITEMS && (
          <p className="text-xs text-muted-foreground mt-1">
            {totalPages > 1
              ? `Showing ${page * MAX_RENDERED_SERIES + 1} to ${Math.min((page + 1) * MAX_RENDERED_SERIES, totalSeriesCount)} of ${totalSeriesCount} series. Hover for details.`
              : `${totalSeriesCount} series. Hover for details.`}
          </p>
        )}
      </CardContent>
    </Card>
  );
});

export const DatadogMetricsChart: React.FC<DatadogMetricsChartProps> = ({ metrics }) => {
  if (!metrics || metrics.length === 0) return null;

  return (
    <div className="space-y-2">
      {metrics.map((metric, i) => (
        <SingleMetricChart key={i} metric={metric} />
      ))}
    </div>
  );
};

export default DatadogMetricsChart;
